import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Emblem from "../components/Emblem";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { startAuthentication } from "@simplewebauthn/browser";
import { Fingerprint } from "lucide-react";
import { PasswordInput } from "../components/PasswordInput";
import { toast } from "sonner";

const LINK_REASON = {
  expired: "This link has expired. Ask an officer to resend your invite, or use “Forgot password” below.",
  used: "This link was already used. Sign in, or use “Forgot password”.",
};
function Shell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1622487560941-7f6bd5ed24b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')" }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative flex h-full flex-col justify-between p-12 text-parchment">
          <Link to="/" className="flex items-center gap-3"><Emblem size={52} /><span className="font-serif-display text-xl font-bold text-gold">Okuapemman Association of Massachusetts</span></Link>
          <div>
            <p className="font-serif-display text-5xl font-bold leading-tight">Ofie! <span className="text-gold">Biakoye!</span></p>
            <p className="mt-3 text-parchment/80">Home! Unity! One family, from Okuapem to Massachusetts.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-parchment p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><Emblem size={64} /></div>
          <h1 className="font-serif-display text-4xl font-bold text-crimson">{title}</h1>
          <p className="mt-2 text-muted-foreground">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const u = await login(form.email, form.password);
      toast.success(`Akwaaba, ${u.name}!`);
      nav(loc.state?.from || "/portal", { replace: true });
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const { setUser } = useAuth();
  const passkey = async () => {
    setBusy(true); setError("");
    try {
      const { data: opts } = await api.post("/passkeys/login/options");
      const credential = await startAuthentication({ optionsJSON: opts });
      const { data: u } = await api.post("/passkeys/login/complete", { credential });
      setUser(u); toast.success(`Akwaaba, ${u.name}!`); nav(loc.state?.from || "/portal", { replace: true });
    } catch (err) { if (err?.name !== "NotAllowedError") setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" subtitle="Sign in to your member portal.">
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        <div><Label>Email</Label><Input type="email" required value={form.email} data-testid="login-email"
          onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><Label>Password</Label><PasswordInput required value={form.password} testId="login-password"
          onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full bg-crimson hover:bg-crimson-hover" data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        {typeof window !== "undefined" && window.PublicKeyCredential && (
          <Button type="button" variant="outline" disabled={busy} onClick={passkey} className="w-full gap-2 border-gold text-crimson" data-testid="login-passkey"><Fingerprint size={16} /> Sign in with Face ID / passkey</Button>
        )}
        <p className="text-center text-xs text-muted-foreground" data-testid="login-policy-note">By continuing you agree to our <Link to="/terms" className="underline">Terms of Use</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-palm hover:underline">Forgot password?</Link>
          <Link to="/registration" className="text-crimson hover:underline" data-testid="to-register">Become a member</Link>
        </div>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await register(form);
      toast.success("Account created! Akwaaba!");
      nav("/portal", { replace: true });
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account" subtitle="Join the Okuapemman family online.">
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        <div><Label>Full name</Label><Input required value={form.name} data-testid="register-name"
          onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label>Email</Label><Input type="email" required value={form.email} data-testid="register-email"
          onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><Label>Phone</Label><Input value={form.phone} data-testid="register-phone"
          onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        <div><Label>Password</Label><PasswordInput required value={form.password} testId="register-password"
          onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full bg-crimson hover:bg-crimson-hover" data-testid="register-submit">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <p className="text-center text-sm">Already a member? <Link to="/login" className="text-crimson hover:underline" data-testid="to-login">Sign in</Link></p>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ token: params.get("token") || "", password: "" });
  const [error, setError] = useState("");
  const [linkState, setLinkState] = useState(null);
  useEffect(() => {
    const tok = params.get("token");
    if (!tok) return;
    import("../lib/api").then(({ default: api }) => api.get("/auth/reset-password/check", { params: { token: tok } }).then(({ data }) => setLinkState(data)).catch(() => {}));
  }, [params]);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    try {
      const api = (await import("../lib/api")).default;
      await api.post("/auth/reset-password", { token: form.token, password: form.password });
      nav("/login?reset=1");
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
  };
  return (
    <Shell title="Set your password" subtitle="Enter the code from your email and choose a new password.">
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
        {linkState && !linkState.valid && (
          <p className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="reset-link-expired">
            {LINK_REASON[linkState.reason] || "This link is not valid."}
          </p>
        )}
        <div><Label>Reset code</Label><Input required value={form.token} data-testid="reset-token" onChange={(e) => setForm({ ...form, token: e.target.value })} /></div>
        <div><Label>New password</Label><PasswordInput required minLength={8} value={form.password} testId="reset-password" onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <Button type="submit" className="w-full bg-crimson hover:bg-crimson-hover" data-testid="reset-submit">Save password</Button>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("../lib/api")).default;
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  };
  return (
    <Shell title="Reset password" subtitle="We'll email you a reset code.">
      {sent ? (
        <div className="mt-8 space-y-3"><p className="rounded-lg bg-palm-light p-4 text-palm" data-testid="forgot-sent">If that email exists, a reset code has been sent. Check your inbox.</p>
          <p className="text-center text-sm"><Link to="/reset-password" className="text-crimson hover:underline" data-testid="to-reset">I have my code</Link></p></div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
          <div><Label>Email</Label><Input type="email" required value={email} data-testid="forgot-email" onChange={(e) => setEmail(e.target.value)} /></div>
          <Button type="submit" className="w-full bg-crimson hover:bg-crimson-hover" data-testid="forgot-submit">Send reset code</Button>
          <p className="text-center text-sm"><Link to="/login" className="text-crimson hover:underline">Back to sign in</Link></p>
        </form>
      )}
    </Shell>
  );
}
